import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout/Layout';
import incidentsService from '../services/incidents';
import './IncidentDetail.scss';

const IncidentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [updatingComment, setUpdatingComment] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const [newChatMessage, setNewChatMessage] = useState('');
  const [submittingChat, setSubmittingChat] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  useEffect(() => {
    const loadIncident = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await incidentsService.getIncidentById(id);
        setIncident(data);
      } catch (err) {
        console.error('Error al cargar incidente:', err);
        setError('Error al cargar el incidente');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadIncident();
    }
  }, [id]);

  useEffect(() => {
    const loadComments = async () => {
      if (!id) return;

      try {
        setLoadingComments(true);
        const commentsData = await incidentsService.getTaskComments(id);
        setComments(commentsData);
      } catch (err) {
        console.error('Error al cargar comentarios:', err);
      } finally {
        setLoadingComments(false);
      }
    };

    if (id && incident) {
      loadComments();
    }
  }, [id, incident]);

  useEffect(() => {
    const loadAttachments = async () => {
      if (!id) return;

      try {
        setLoadingAttachments(true);
        const data = await incidentsService.getTaskAttachments(id);
        setAttachments(data);
      } catch (err) {
        console.error('Error al cargar adjuntos:', err);
      } finally {
        setLoadingAttachments(false);
      }
    };

    if (id && incident) {
      loadAttachments();
    }
  }, [id, incident]);

  useEffect(() => {
    const loadChat = async () => {
      if (!id) return;

      try {
        setLoadingChat(true);
        const chatData = await incidentsService.getTaskChatMessages(id);
        setChatMessages(chatData);
      } catch (err) {
        console.error('Error al cargar chat:', err);
      } finally {
        setLoadingChat(false);
      }
    };

    if (id && incident) {
      loadChat();
    }
  }, [id, incident]);

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'Fecha no disponible';
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Fecha inválida';
      }
      return date.toLocaleString('es-AR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return 'Fecha inválida';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'P1':
        return 'priority-high';
      case 'P2':
        return 'priority-medium';
      case 'P3':
        return 'priority-low';
      case 'P4':
        return 'priority-default';
      default:
        return 'priority-default';
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submittingComment) return;

    try {
      setSubmittingComment(true);
      await incidentsService.createTaskComment(id, newComment.trim());
      setNewComment('');
      // Recargar comentarios
      const commentsData = await incidentsService.getTaskComments(id);
      setComments(commentsData);
    } catch (err) {
      console.error('Error al crear comentario:', err);
      alert('Error al crear el comentario. Por favor, intente nuevamente.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const getUserName = (createdBy) => {
    if (!createdBy) return 'Usuario desconocido';
    if (typeof createdBy === 'object') {
      // Si es un objeto, intentar obtener username, email o first_name/last_name
      if (createdBy.email) return createdBy.email;
      if (createdBy.first_name || createdBy.last_name) {
        return `${createdBy.first_name || ''} ${createdBy.last_name || ''}`.trim() || 'Usuario';
      }
      return 'Usuario';
    }
    // Si es solo un ID (número), mostrar un mensaje genérico
    return `Usuario #${createdBy}`;
  };

  const handleEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.comment);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const handleSaveComment = async (commentId) => {
    if (!editingCommentText.trim() || updatingComment) return;

    try {
      setUpdatingComment(true);
      await incidentsService.updateTaskComment(id, commentId, editingCommentText.trim());
      setEditingCommentId(null);
      setEditingCommentText('');
      // Recargar comentarios
      const commentsData = await incidentsService.getTaskComments(id);
      setComments(commentsData);
    } catch (err) {
      console.error('Error al actualizar comentario:', err);
      alert('Error al actualizar el comentario. Por favor, intente nuevamente.');
    } finally {
      setUpdatingComment(false);
    }
  };

  const handleSubmitChatMessage = async (e) => {
    e.preventDefault();
    if (!newChatMessage.trim() || submittingChat) return;

    const confirmed = window.confirm('¿Enviar este mensaje al cliente?');
    if (!confirmed) {
      return;
    }

    try {
      setSubmittingChat(true);
      await incidentsService.createTaskChatMessage(id, newChatMessage.trim());
      setNewChatMessage('');
      const chatData = await incidentsService.getTaskChatMessages(id);
      setChatMessages(chatData);
    } catch (err) {
      console.error('Error al enviar mensaje de chat:', err);
      alert('Error al enviar el mensaje. Intente nuevamente.');
    } finally {
      setSubmittingChat(false);
    }
  };

  const formatFileSize = (size) => {
    if (!size || Number.isNaN(size)) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = Number(size);
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024;
      unit += 1;
    }
    return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
  };

  const handleUploadAttachment = async (e) => {
    e.preventDefault();
    if (!selectedAttachment || uploadingAttachment) return;

    try {
      setUploadingAttachment(true);
      await incidentsService.uploadTaskAttachment(id, selectedAttachment);
      setSelectedAttachment(null);
      const fileInput = document.getElementById('staff-attachment-input');
      if (fileInput) {
        fileInput.value = '';
      }
      const data = await incidentsService.getTaskAttachments(id);
      setAttachments(data);
    } catch (err) {
      console.error('Error al subir adjunto:', err);
      alert('No se pudo subir el archivo. Verifique el tipo y tamaño permitido.');
    } finally {
      setUploadingAttachment(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="incident-detail-container">
          <div className="container">
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Cargando incidente...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !incident) {
    return (
      <Layout>
        <div className="incident-detail-container">
          <div className="container">
            <div className="error-state">
              <div className="error-icon">❌</div>
              <h3>Error al cargar el incidente</h3>
              <p>{error || 'El incidente solicitado no existe'}</p>
              <button
                className="btn btn-primary"
                onClick={() => navigate('/incidentes')}
              >
                Volver a crear incidente
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="incident-detail-container">
        <div className="container">
          <div className="incident-header">
            <div className="header-content">
              <h1>{incident.title || `Incidente #${incident.id}`}</h1>
              <p>
                {incident.description
                  ? incident.description
                  : 'La tarea no tiene una descripción disponible'}
              </p>
            </div>
            <div className="header-actions">
              <button style={{ marginRight: '16px' }}
                className="btn btn-primary mr-16px"
                onClick={() => navigate(`/admin/questions/task/incidentes?edit=${incident.id}`)}
              >
                Editar incidente
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate('/admin/questions/task/incidentes')}
              >
                Crear nuevo incidente
              </button>
            </div>
          </div>

          <div className="incident-content">
            {/* Información principal */}
            <div className="incident-card main-info">
              <div className="card-header">
                <h3>Información Principal</h3>
                <span className="creation-date">Creado el {formatDate(incident.created_at)}</span>
              </div>
              <div className="card-content">
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">ID:</span>
                    <span className="value">#{incident.id}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Estado:</span>
                    <span
                      className="value state-badge state-badge-dynamic"
                      style={{
                        color: incident.stateColor || '#000000',
                        backgroundColor: incident.stateBackgroundColor || '#f0f0f0',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        display: 'inline-block'
                      }}
                    >
                      {incident.state || 'No especificado'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Prioridad:</span>
                    <span className={`value priority-badge ${getPriorityColor(incident.priority)}`}>
                      {incident.priority || 'No especificado'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Canal:</span>
                    <span className="value">{incident.channel || 'No especificado'}</span>
                  </div>
                </div>
              </div>
            </div>


            {/* Grid con 3 tarjetas en una fila */}
            <div className="incident-grid">
              {/* Clasificación */}
              <div className="incident-card classification-card">
                <div className="card-header">
                  <h3>Clasificación</h3>
                </div>
                <div className="card-content">
                  <div className="classification-info classification-info--stacked">
                    <div className="classification-item full-width">
                      <span className="label">Categoría</span>
                      <span className="value">{incident.classification?.category || 'No especificado'}</span>
                    </div>
                    <div className="classification-item full-width">
                      <span className="label">Responsable Asignado</span>
                      <span className="value">
                        {incident.assignedTo ? (
                          <span>
                            {incident.assignedTo.fullName}
                            {incident.assignedTo.email && (
                              <span style={{ color: '#6b7280', fontSize: '0.875rem', marginLeft: '8px' }}>
                                ({incident.assignedTo.email})
                              </span>
                            )}
                          </span>
                        ) : (
                          <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No asignado</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contacto */}
              <div className="incident-card contact-card">
                <div className="card-header">
                  <h3>Información del Contacto</h3>
                </div>
                <div className="card-content">
                  {incident.contact ? (
                    <div className="contact-info contact-info--grid">
                      <div className="contact-row contact-row--name">
                        <div className="contact-item">
                          <span className="label">Nombre</span>
                          <span className="value">{incident.contact.first_name || 'No especificado'}</span>
                        </div>
                        <div className="contact-item">
                          <span className="label">Apellido</span>
                          <span className="value">{incident.contact.last_name || 'No especificado'}</span>
                        </div>
                      </div>

                      <div className="contact-row contact-row--email">
                        <div className="contact-item full-width">
                          <span className="label">Email</span>
                          <span className="value">{incident.contact.email || 'No especificado'}</span>
                        </div>
                      </div>

                      <div className="contact-row contact-row--phones">
                        <div className="contact-item">
                          <span className="label">Teléfono 1</span>
                          <span className="value">{incident.contact.phone || 'No especificado'}</span>
                        </div>
                        {incident.contact.phone2 ? (
                          <div className="contact-item">
                            <span className="label">Teléfono 2</span>
                            <span className="value">{incident.contact.phone2}</span>
                          </div>
                        ) : null}
                      </div>

                      <div className="contact-row contact-row--address">
                        <div className="contact-item full-width">
                          <span className="label">Dirección</span>
                          <span className="value">{incident.contact.address || 'No especificado'}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="no-data">No hay información de contacto disponible</p>
                  )}
                </div>
              </div>

              <div className="incident-card attachments-card compact-attachments">
                <div className="card-header">
                  <h3>Documentación</h3>
                  <span className="comments-count">{attachments.length} archivo{attachments.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="card-content">
                  <form onSubmit={handleUploadAttachment} className="attachments-upload-row">
                    <input
                      id="staff-attachment-input"
                      type="file"
                      onChange={(e) => setSelectedAttachment(e.target.files?.[0] || null)}
                      className="attachments-hidden-input"
                      disabled={uploadingAttachment}
                    />
                    <div className="attachments-picker">
                      <label htmlFor="staff-attachment-input" className="attachments-pick-btn">
                        Seleccionar archivo
                      </label>
                      <span className={`attachments-picked ${selectedAttachment ? 'has-file' : ''}`}>
                        {selectedAttachment ? selectedAttachment.name : 'Ningún archivo seleccionado'}
                      </span>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={!selectedAttachment || uploadingAttachment}
                    >
                      {uploadingAttachment ? 'Subiendo...' : 'Subir'}
                    </button>
                  </form>

                  <div className="attachments-list">
                    {loadingAttachments ? (
                      <div className="no-comments">Cargando adjuntos...</div>
                    ) : attachments.length === 0 ? (
                      <div className="no-comments">No hay archivos adjuntos.</div>
                    ) : (
                      attachments.map((item) => (
                        <a
                          key={item.id}
                          href={item.file_url || item.file}
                          target="_blank"
                          rel="noreferrer"
                          className="attachment-item"
                        >
                          <span className="name">{item.original_name || 'Archivo'}</span>
                          <span className="meta">{formatFileSize(item.size)} · {formatDate(item.created_at)}</span>
                        </a>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Descripción */}
              {/* <div className="incident-card description-card">
                <div className="card-header">
                  <h3>Descripción</h3>
                </div>
                <div className="card-content">
                  <div className="description-content">
                    <p>{incident.description || 'Sin descripción'}</p>
                  </div>
                </div>
              </div> */}
            </div>

            {/* Campos personalizados */}
            {incident.customFields && incident.customFields.length > 0 && (
              <div className="incident-card custom-fields-card">
                <div className="card-header">
                  <h3>Campos personalizados</h3>
                </div>
                <div className="card-content">
                  <div className="custom-fields-info">
                    <div className="custom-fields-row">
                      {incident.customFields.map((field) => (
                        <div key={field.id} className="custom-fields-item">
                          <span className="label">{field.name}</span>
                          <span className="value">{field.value || 'No especificado'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Comentarios */}
            <div className="incident-card comments-card compact-comments">
              <div className="card-header">
                <h3>Comentarios</h3>
                <span className="comments-count">{comments.length} comentario{comments.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="card-content">
                <div className="comments-panel">
                  <div className="comments-list-compact" role="log" aria-live="polite">
                    {loadingComments ? (
                      <div className="loading-comments">
                        <p>Cargando comentarios...</p>
                      </div>
                    ) : comments.length === 0 ? (
                      <div className="no-comments">
                        <p>Aún no hay comentarios en este incidente.</p>
                      </div>
                    ) : (
                      comments.map((comment) => (
                        <div key={comment.id} className="comment-message-row">
                          <div className="comment-meta-line">
                            <span className="author">{getUserName(comment.created_by)}</span>
                            <span className="dot">•</span>
                            <span className="time">{formatDate(comment.created_at)}</span>
                            {editingCommentId !== comment.id && (
                              <button
                                type="button"
                                className="btn-edit-comment"
                                onClick={() => handleEditComment(comment)}
                                title="Editar comentario"
                              >
                                ✏️
                              </button>
                            )}
                          </div>
                          <div className="comment-bubble">
                            {editingCommentId === comment.id ? (
                              <div className="comment-edit-form compact">
                                <textarea
                                  value={editingCommentText}
                                  onChange={(e) => setEditingCommentText(e.target.value)}
                                  className="comment-edit-input"
                                  rows="2"
                                  disabled={updatingComment}
                                />
                                <div className="comment-edit-actions">
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleSaveComment(comment.id)}
                                    disabled={!editingCommentText.trim() || updatingComment}
                                  >
                                    {updatingComment ? 'Guardando...' : 'Guardar'}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={handleCancelEdit}
                                    disabled={updatingComment}
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p>{comment.comment}</p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <form onSubmit={handleSubmitComment} className="comments-composer-row">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Escribe un comentario..."
                      rows="2"
                      className="comment-input"
                      disabled={submittingComment}
                    />
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={!newComment.trim() || submittingComment}
                    >
                      {submittingComment ? 'Enviando...' : 'Agregar'}
                    </button>
                  </form>
                </div>
              </div>
            </div>

            <div className="incident-card chat-card">
              <div className="card-header">
                <h3>Chat con cliente</h3>
                <span className="comments-count">{chatMessages.length} mensaje{chatMessages.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="card-content">
                {incident.contact ? (
                  <div className="chat-panel">
                    <div className="chat-list" role="log" aria-live="polite">
                      {loadingChat ? (
                        <div className="loading-comments">
                          <p>Cargando chat...</p>
                        </div>
                      ) : chatMessages.length === 0 ? (
                        <div className="no-comments">
                          <p>Aún no hay mensajes con el cliente.</p>
                        </div>
                      ) : (
                        chatMessages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`chat-message-row ${msg.is_from_contact ? 'from-contact' : 'from-staff'}`}
                          >
                            <div className="chat-meta-line">
                              <span className="author">{msg.is_from_contact ? 'Contacto' : getUserName(msg.author)}</span>
                              <span className="dot">•</span>
                              <span className="time">{formatDate(msg.created_at)}</span>
                            </div>
                            <div className="chat-bubble">{msg.message}</div>
                          </div>
                        ))
                      )}
                    </div>

                    <form onSubmit={handleSubmitChatMessage} className="chat-composer-row">
                      <textarea
                        value={newChatMessage}
                        onChange={(e) => setNewChatMessage(e.target.value)}
                        placeholder="Escribe un mensaje para el cliente..."
                        rows="2"
                        className="chat-input"
                        disabled={submittingChat}
                      />
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!newChatMessage.trim() || submittingChat}
                      >
                        {submittingChat ? 'Enviando...' : 'Enviar'}
                      </button>
                    </form>
                  </div>
                ) : (
                  <p className="no-data">Esta tarea no tiene cliente asociado.</p>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default IncidentDetail;
